import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, pairwise, startWith, switchMap, catchError, of, EMPTY, withLatestFrom } from 'rxjs';
import { routerSegments } from '../../consts/routes';
import { AccountsMapActions } from '../state/accounts-map/accounts-map.actions';
import { AccountsService } from '../../services/accounts.service';
import { accountsMapFeature } from '../state/accounts-map/accounts-map.feature';
import { AccountMapEntry } from '../state/accounts-map/accounts-map.models';

const isHomeUrl = (url: string): boolean => url.startsWith(`/${routerSegments.home}`);

export const loadAccountsMapOnHomeEnter = createEffect(
  (router = inject(Router)) => {
    return router.events.pipe(
      filter((event): event is NavigationEnd | NavigationSkipped =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
      ),
      map(() => router.url),
      startWith(router.url),
      pairwise(),
      filter(([prev, curr]) => !isHomeUrl(prev) && isHomeUrl(curr)),
      map(() => AccountsMapActions.loadAccountsMap()),
    );
  },
  { functional: true },
);

export const resetAccountsMapOnHomeLeave = createEffect(
  (router = inject(Router)) => {
    return router.events.pipe(
      filter((event): event is NavigationEnd | NavigationSkipped =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
      ),
      map(() => router.url),
      startWith(router.url),
      pairwise(),
      filter(([prev, curr]) => isHomeUrl(prev) && !isHomeUrl(curr)),
      map(() => AccountsMapActions.resetAccountsMap()),
    );
  },
  { functional: true },
);

export const loadAccountsMap = createEffect(
  (
    actions$ = inject(Actions),
    accountsService = inject(AccountsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(AccountsMapActions.loadAccountsMap),
      withLatestFrom(store.select(accountsMapFeature.selectLoaded)),
      switchMap(([, loaded]) => {
        if (loaded) {
          return EMPTY;
        }
        return accountsService.getAccounts().pipe(
          map((response) => {
            const accounts: AccountMapEntry[] = [];
            response.organizations.forEach((org) => {
              org.accounts.forEach((account) => {
                accounts.push({
                  id: `${account.id}`,
                  name: account.name,
                  notes: account.notes,
                  organizationId: org.id,
                  organizationName: org.name,
                  address: account.address,
                });
                account.partitions?.forEach((partition) => {
                  accounts.push({
                    id: `${partition.id}`,
                    name: partition.name,
                    organizationId: org.id,
                    organizationName: org.name,
                    parentAccountId: `${account.id}`,
                    parentAccountName: account.name,
                    sensitivityLevel: partition.sensitivityLevel,
                    address: partition?.address || account.address,
                  });
                });
              });
            });
            return AccountsMapActions.loadAccountsMapSuccess({ accounts });
          }),
          catchError((error: Error) =>
            of(AccountsMapActions.loadAccountsMapFail({ error: error.message })),
          ),
        );
      }),
    );
  },
  { functional: true },
);
